import { Elements } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import { memo, useCallback, useMemo } from 'react';

import 'react-loading-skeleton/dist/skeleton.css';

import { useFeature } from '@features/FeatureToggle';
import {
  CheckoutSelector,
  LearnMoreList,
  type TpStripePlanCardProps,
  useGetStripePromise,
} from '@features/Stripe';
import { currencyReplacer } from 'src/utils/payment/currency-replacer';
import { SubscriptionCardMaster } from '../master';
import { CurrentPrice } from '../shared/current-price';
import { DiscountPercentage } from '../shared/discount-percentage';
import { FullPrice } from '../shared/full-price';
import { PriceUnitFull } from '../shared/price-unit-full';
import { UsersCompare } from '../shared/users-compare';

import styles from './index.module.scss';

export const Combo = memo((props: TpStripePlanCardProps) => {
  const { inputIdPrefix, plan, wasPaymentTried, planCardClassname } = props;

  const stripePromise = useGetStripePromise();
  const isIntroOfferEnabled = useFeature('introOffer');

  const currencySymbol = currencyReplacer(plan?.currency);

  const dataMemoized = useMemo(
    () =>
      isIntroOfferEnabled
        ? {
            fullPrice: plan?.introOfferData?.fullPrice,
            fullPriceDiscount: plan?.introOfferData?.fullPriceDiscount,
          }
        : {
            fullPrice: plan?.fullPrice,
            fullPriceDiscount: plan?.fullPriceDiscount,
          },
    [
      isIntroOfferEnabled,
      plan?.introOfferData,
      plan?.fullPrice,
      plan?.fullPriceDiscount,
    ],
  );

  const renderPaymentComponent = useCallback(
    () => (
      <Elements stripe={stripePromise}>
        <CheckoutSelector isCompactStyle />
      </Elements>
    ),
    [stripePromise],
  );

  return (
    <SubscriptionCardMaster
      {...props}
      descriptionEndView
      inputIdPrefix={inputIdPrefix}
      planCardClassname={classNames(styles.planCard, planCardClassname)}
      contentClassname={styles.content}
      leftBottomContent={(value: boolean) => (
        <div className={styles.wrapper}>
          <div
            className={classNames(styles.text, {
              [styles.textChecked]: value,
            })}
          >
            {plan?.fractionalPrice !== plan?.fractionalPriceDiscount && (
              <FullPrice
                currencySymbol={currencySymbol}
                price={dataMemoized.fullPrice}
              />
            )}{' '}
            <FullPrice
              price={dataMemoized.fullPriceDiscount}
              currencySymbol={currencySymbol}
              withDiscount
            />
          </div>
          <div className={styles.discountPercentage}>
            {(plan?.discountPercentage ?? 0) > 0 && (
              <DiscountPercentage
                discountPercentage={plan.discountPercentage ?? 0}
                isActive={value}
                wasPaymentTried={wasPaymentTried}
                size="small"
              />
            )}
          </div>
        </div>
      )}
      rightContent={(value: boolean) => (
        <div className={styles.alignEnd}>
          <div className={styles.rightBlockContainer}>
            {value && (
              <div className={styles.fullPriceAndDiscount}>
                {plan?.fractionalPrice !== plan?.fractionalPriceDiscount && (
                  <PriceUnitFull
                    plan={plan}
                    checked={value}
                    className={styles.priceUnitFull}
                  />
                )}
              </div>
            )}
            <CurrentPrice plan={plan} checked={value} />
          </div>
        </div>
      )}
      fullWidthBottomContent={(value: boolean) =>
        value ? (
          <div className={styles.bottomContent}>
            {plan?.learnMoreList && (
              <LearnMoreList
                learnMoreList={plan.learnMoreList}
                variant="secondary"
              />
            )}
            {plan?.usersCompareBadge && <UsersCompare />}
            {renderPaymentComponent()}
            {/*{isIntroOfferEnabled && (*/}
            {/*  <CardAnnotation*/}
            {/*    planName={plan.name}*/}
            {/*    fullPriceDiscount={plan.fullPriceDiscount}*/}
            {/*    currency={plan.currency}*/}
            {/*  />*/}
            {/*)}*/}
          </div>
        ) : null
      }
    />
  );
});
